import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { DragEvent } from 'react';
import { useArrastrePuntero, type ArrastrePuntero } from './useArrastrePuntero';

/**
 * Arrastre NATIVO HTML5 del compositor (Lote 2b) — sin librerías.
 *
 * El clic sigue siendo primera clase (Lote 2a): este hook solo AGREGA el
 * gesto de arrastrar herramientas del catálogo y fichas 'preparada' hacia
 * las sesiones del lienzo. Caveat conocido: el DnD nativo cojea en
 * Safari/touch, por eso nunca reemplaza a los botones existentes.
 *
 * Los targets de drop NO leen dataTransfer en dragover (el navegador lo
 * bloquea hasta el drop): validan contra el ítem en vuelo que este hook
 * guarda en estado React y comparte por {@link ArrastreContext}.
 */

/** MIME propio del compositor: distingue nuestros drags de texto/archivos ajenos. */
export const MIME_AGORA = 'application/x-agora';

/** Ítem en vuelo durante un arrastre del compositor. */
export type ItemArrastre =
  | { tipo: 'herramienta'; codigo: string }
  | { tipo: 'actividad'; id: string; sesionId: string };

export interface ArrastreCompositor {
  /** Ítem en vuelo, o null si no hay arrastre en curso. */
  item: ItemArrastre | null;
  /** Fábrica de onDragStart para una TarjetaHerramienta del catálogo. */
  arrastrarHerramienta: (codigo: string) => (e: DragEvent) => void;
  /** Fábrica de onDragStart para una FichaActividad 'preparada'. */
  arrastrarActividad: (id: string, sesionId: string) => (e: DragEvent) => void;
  /** onDragEnd / limpieza tras soltar: apaga el ítem en vuelo. */
  terminarArrastre: () => void;
  /**
   * La vía del dedo. El arrastre nativo de HTML no ocurre en un teléfono, así
   * que sin esto el compositor era una pantalla de solo lectura en móvil.
   * Alimenta el MISMO ítem en vuelo, de modo que los destinos no distinguen —
   * ni tienen por qué— si lo que viene arrastrado viene de un ratón o de un
   * pulgar. Ver `useArrastrePuntero`.
   */
  puntero: ArrastrePuntero;
}

export function useArrastre(): ArrastreCompositor {
  const [item, setItem] = useState<ItemArrastre | null>(null);
  const puntero = useArrastrePuntero();

  const iniciar = useCallback(
    (nuevo: ItemArrastre) => (e: DragEvent) => {
      e.dataTransfer.setData(MIME_AGORA, JSON.stringify(nuevo));
      e.dataTransfer.effectAllowed = 'move';
      // Diferido: mutar el DOM del nodo arrastrado DENTRO de dragstart
      // aborta el drag en Chromium; con el timeout el gesto ya arrancó.
      setTimeout(() => setItem(nuevo), 0);
    },
    [],
  );

  const arrastrarHerramienta = useCallback(
    (codigo: string) => iniciar({ tipo: 'herramienta', codigo }),
    [iniciar],
  );
  const arrastrarActividad = useCallback(
    (id: string, sesionId: string) => iniciar({ tipo: 'actividad', id, sesionId }),
    [iniciar],
  );
  const terminarArrastre = useCallback(() => setItem(null), []);

  return useMemo(
    () => ({
      // El ítem en vuelo puede venir del arrastre nativo o del dedo: para los
      // destinos es exactamente lo mismo.
      item: item ?? puntero.levantado,
      arrastrarHerramienta,
      arrastrarActividad,
      terminarArrastre,
      puntero,
    }),
    [item, arrastrarHerramienta, arrastrarActividad, terminarArrastre, puntero],
  );
}

/**
 * Contexto que baja el arrastre de CompositorPage a catálogo y fichas sin
 * tocar los componentes intermedios. Null = superficie sin drag (el clic
 * del 2a sigue funcionando igual).
 */
export const ArrastreContext = createContext<ArrastreCompositor | null>(null);

/** Arrastre del compositor, o null fuera del Provider. */
export function useArrastreCompositor(): ArrastreCompositor | null {
  return useContext(ArrastreContext);
}
